import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, {
  forwardRef,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import type { HTMLAttributes } from 'react';
import { Outlet, useLocation } from 'react-router';
import { IsSidebarOpenProvider } from './IsSidebarOpenContext';
import type { OutletContextValue } from './Page';
import * as styles from './PageLayout.module.scss';
import {
  useShouldShiftContentForSidebar,
  useSidebarShiftBreakpoint,
} from './SidebarShiftStateProvider';
import AppBanner from '../../banners/AppBanner';
import ModalContainer from '../../components/ModalContainer';
import ToastContainer from '../../components/ToastContainer';
import VersionMismatchModal from '../../error/VersionMismatchModal';
import { UiThemeSetting } from '../../graphql/generated/graphql';
import OnboardingContainer from '../../onboarding/OnboardingContainer';
import { UIActions } from '../../ui/uiSlice';
import { isElectron } from '../../utils/desktopClient';
import PageMeta from '../../utils/PageMeta';
import useIsDarkMode from '../../utils/useIsDarkMode';
import useAppSelector, { useAppDispatch } from '../redux/useAppSelector';

export const SIDEBAR_TRANSITION_DURATION = parseInt(
  styles.sidebarTransitionDuration,
  10,
);

export interface PageLayoutElements {
  sidebar?: JSX.Element;
  helpMenu?: JSX.Element;
}

interface PageLayoutProps {
  elements: PageLayoutElements;
  routeName: string;
}

const Root = ({
  elements: { sidebar, helpMenu },
  routeName,
}: PageLayoutProps) => {
  const isDarkMode = useIsDarkMode();

  useLayoutEffect(() => {
    document.body.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');

    if (isElectron) {
      window.desktop?.changeTheme?.(
        isDarkMode ? UiThemeSetting.Dark : UiThemeSetting.Light,
      );
    }

    return () => {
      document.body.removeAttribute('data-theme');
    };
  }, [isDarkMode]);

  const dispatch = useAppDispatch();

  const shouldShiftContentForSidebar = useShouldShiftContentForSidebar();

  const hasInitializedSidebarStateRef = useRef(false);
  const sidebarShiftBreakpoint = useSidebarShiftBreakpoint();

  useEffect(() => {
    if (sidebar && !hasInitializedSidebarStateRef.current) {
      dispatch(
        UIActions.toggleSidebar(window.innerWidth >= sidebarShiftBreakpoint),
      );
      hasInitializedSidebarStateRef.current = true;
    }
  }, [sidebar, dispatch, sidebarShiftBreakpoint]);

  const isSidebarOpenPreferred = useAppSelector(
    (state) => state.ui.isSidebarOpenPreferred,
  );
  const isSidebarOpen = isSidebarOpenPreferred && !!sidebar;

  const location = useLocation();

  useEffect(() => {
    if (sidebar && window.innerWidth < sidebarShiftBreakpoint) {
      dispatch(UIActions.toggleSidebar(false));
    }
  }, [sidebar, location, dispatch, sidebarShiftBreakpoint]);

  return (
    <IsSidebarOpenProvider isSidebarOpen={isSidebarOpen}>
      <div
        id={`${routeName}-view`}
        className={classNames('page-layout-root', {
          [styles.isSidebarOpen]: isSidebarOpen,
          [styles.shouldShiftContentForSidebar]: shouldShiftContentForSidebar,
        })}
      >
        <PageMeta title={`${_.startCase(routeName)} - Slab`} />
        <AppBanner />
        <ModalContainer />
        <VersionMismatchModal />
        <ToastContainer />
        {sidebar}
        <main className={classNames('page-layout-main', styles.main)}>
          <Suspense fallback={null}>
            <Outlet
              context={
                {
                  helpMenu,
                } satisfies OutletContextValue
              }
            />
          </Suspense>
        </main>
        <div
          className={styles.overlay}
          onClick={() => {
            if (isSidebarOpen) {
              dispatch(UIActions.toggleSidebar(false));
            }
          }}
        />
        <OnboardingContainer />
      </div>
    </IsSidebarOpenProvider>
  );
};

interface SidebarProps extends HTMLAttributes<HTMLElement> {
  publicVariant?: boolean;
}

const Sidebar = ({ publicVariant, className, ...props }: SidebarProps) => (
  <nav
    className={classNames(styles.sidebar, className, {
      [styles.isPublicVariant]: publicVariant,
    })}
    {...props}
  />
);

export const TOPBAR_NODE_ID = 'pageLayoutTopbar';
const Topbar = forwardRef<
  HTMLHeadingElement,
  Omit<HTMLAttributes<HTMLHeadingElement>, 'id' | 'role' | 'aria-label'>
>(({ className, ...props }, ref) => (
  <header
    ref={ref}
    id={TOPBAR_NODE_ID}
    role="banner"
    aria-label="Topbar"
    className={classNames(styles.topbar, className)}
    {...props}
  />
));

interface ContentProps extends Omit<HTMLAttributes<HTMLDivElement>, 'id'> {
  noPaddingX?: boolean;
  noPaddingY?: boolean;
}

const Content = ({
  noPaddingX,
  noPaddingY,
  className,
  ...props
}: ContentProps) => (
  <div
    id="content"
    className={classNames(styles.content, className, {
      [styles.noPaddingX]: noPaddingX,
      [styles.noPaddingY]: noPaddingY,
    })}
    {...props}
  />
);

const Inner = (props: HTMLAttributes<HTMLDivElement>) => (
  <div className={classNames('page-layout-inner', styles.inner)} {...props} />
);

export default { Content, Inner, Root, Sidebar, Topbar };
